import type { Candle } from '../types/market.js';
import { mean } from './number.js';

export function simpleMovingAverage(values: number[], period: number): number[] {
  if (period <= 0) return [];
  return values.map((_, index) => {
    if (index + 1 < period) return Number.NaN;
    return mean(values.slice(index + 1 - period, index + 1));
  });
}

export function exponentialMovingAverage(values: number[], period: number): number[] {
  if (values.length === 0 || period <= 0) return [];
  const multiplier = 2 / (period + 1);
  const output = [values[0] ?? 0];
  for (let index = 1; index < values.length; index += 1) {
    const value = values[index] ?? output[index - 1] ?? 0;
    output.push((value - (output[index - 1] ?? value)) * multiplier + (output[index - 1] ?? value));
  }
  return output;
}

export function relativeStrengthIndex(values: number[], period = 14): number[] {
  const output = new Array<number>(values.length).fill(Number.NaN);
  if (values.length <= period) return output;

  let gains = 0;
  let losses = 0;
  for (let index = 1; index <= period; index += 1) {
    const change = (values[index] ?? 0) - (values[index - 1] ?? 0);
    gains += Math.max(change, 0);
    losses += Math.max(-change, 0);
  }

  let averageGain = gains / period;
  let averageLoss = losses / period;
  output[period] = averageLoss === 0 ? 100 : 100 - 100 / (1 + averageGain / averageLoss);

  for (let index = period + 1; index < values.length; index += 1) {
    const change = (values[index] ?? 0) - (values[index - 1] ?? 0);
    averageGain = (averageGain * (period - 1) + Math.max(change, 0)) / period;
    averageLoss = (averageLoss * (period - 1) + Math.max(-change, 0)) / period;
    output[index] = averageLoss === 0 ? 100 : 100 - 100 / (1 + averageGain / averageLoss);
  }

  return output;
}

export function averageTrueRange(candles: Candle[], period = 14): number[] {
  const ranges = candles.map((candle, index) => {
    const previousClose = candles[index - 1]?.close ?? candle.open;
    return Math.max(candle.high - candle.low, Math.abs(candle.high - previousClose), Math.abs(candle.low - previousClose));
  });
  return simpleMovingAverage(ranges, period);
}
